import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Folder, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { FileRow } from '@/components/ged/FileRow'
import { FileDrawer } from '@/components/ged/FileDrawer'
import { getAreaDocumentTypes, areaKeys } from '@/lib/api/areas'
import { listDocuments, documentKeys } from '@/lib/api/documents'
import { countDescendantAreas } from '@/lib/areaTree'
import { useSelectedDocParam } from '@/lib/useSelectedDocParam'
import type { AreaDetail, AreaNode, ListDocumentsParams } from '@/lib/types/ged'
import { Button } from '@/components/ui/button'

const PAGE_SIZE = 20

type Props = {
  area: AreaDetail
  folders: AreaNode[]
}

export function AreaContentPage({ area, folders }: Props) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [typeId, setTypeId] = useState<string | undefined>(undefined)
  const [notViewedOnly, setNotViewedOnly] = useState(false)
  const { selectedDocId, setSelectedDocId } = useSelectedDocParam()

  const { data: types = [] } = useQuery({
    queryKey: areaKeys.documentTypes(area.id),
    queryFn: () => getAreaDocumentTypes(area.id),
  })

  const params: ListDocumentsParams = {
    areaId: area.id, typeId, search: search || undefined, page, pageSize: PAGE_SIZE,
  }
  const { data, isLoading } = useQuery({
    queryKey: documentKeys.list(params),
    queryFn: () => listDocuments(params),
    placeholderData: (prev) => prev,
  })

  const allFiles = data?.items ?? []
  const files = notViewedOnly ? allFiles.filter((d) => !d.viewedAt) : allFiles
  const totalCount = data?.totalCount ?? 0
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const totalTypeCount = types.reduce((sum, t) => sum + t.documentCount, 0)

  return (
    <div>
      {/* Filtros por tipo */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <Pill active={typeId === undefined} onClick={() => { setTypeId(undefined); setPage(1) }}>
          Tudo {totalTypeCount}
        </Pill>
        {types.map((t) => (
          <Pill key={t.id} active={typeId === t.id} onClick={() => { setTypeId(t.id); setPage(1) }}>
            {t.name} {t.documentCount}
          </Pill>
        ))}
        <Pill active={notViewedOnly} onClick={() => setNotViewedOnly((v) => !v)}>
          Não vistos
        </Pill>
      </div>

      {/* Busca */}
      <div
        style={{
          background: 'var(--ev-bg-elevated)', border: '1px solid var(--ev-border)', borderRadius: 10,
          padding: 12, marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center',
        }}
      >
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--ev-fg-muted)' }} />
          <Input
            placeholder="Buscar nesta pasta..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            style={{ paddingLeft: 32, height: 36 }}
          />
        </div>
      </div>

      {/* Pastas + arquivos */}
      <div style={{ background: 'var(--ev-bg-elevated)', border: '1px solid var(--ev-border)', borderRadius: 10, overflow: 'hidden' }}>
        {folders.map((node) => <FolderRow key={node.id} node={node} />)}
        {isLoading && (
          <div style={{ textAlign: 'center', color: 'var(--ev-fg-muted)', fontSize: 'var(--fs-small)', padding: '32px 0' }}>Carregando…</div>
        )}
        {!isLoading && folders.length === 0 && files.length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--ev-fg-muted)', fontSize: 'var(--fs-small)', padding: '32px 0' }}>
            Nenhum arquivo nesta pasta.
          </div>
        )}
        {files.map((doc) => (
          <FileRow key={doc.id} doc={doc} onClick={setSelectedDocId} density="compact" isSelected={doc.id === selectedDocId} />
        ))}
      </div>

      {/* Paginação */}
      {totalCount > PAGE_SIZE && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, fontSize: 'var(--fs-small)', color: 'var(--ev-fg-muted)' }}>
          <span>Mostrando {Math.min((page - 1) * PAGE_SIZE + 1, totalCount)}–{Math.min(page * PAGE_SIZE, totalCount)} de {totalCount}</span>
          <div style={{ display: 'flex', gap: 4 }}>
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>‹</Button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map((n) => (
              <Button key={n} variant={n === page ? 'default' : 'outline'} size="sm" onClick={() => setPage(n)} style={{ minWidth: 30, padding: 0 }}>
                {n}
              </Button>
            ))}
            <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>›</Button>
          </div>
        </div>
      )}

      <FileDrawer documentId={selectedDocId} onClose={() => setSelectedDocId(null)} />
    </div>
  )
}

function FolderRow({ node }: { node: AreaNode }) {
  const subAreaCount = countDescendantAreas(node)
  return (
    <Link
      to={`/areas/${node.slug}`}
      style={{
        display: 'grid', gridTemplateColumns: '40px 1fr auto', alignItems: 'center', gap: 12,
        padding: '14px 16px', textDecoration: 'none', color: 'inherit',
        borderBottom: '1px solid var(--ev-divider)',
      }}
    >
      <div style={{ width: 32, height: 36, background: 'var(--ev-accent-soft)', color: 'var(--ev-accent)', borderRadius: 4, display: 'grid', placeItems: 'center' }}>
        <Folder size={16} />
      </div>
      <div style={{ fontSize: 'var(--fs-small)', fontWeight: 500, color: 'var(--ev-fg-strong)' }}>{node.name}</div>
      <div style={{ fontSize: 'var(--fs-small)', color: 'var(--ev-fg-muted)' }}>
        {subAreaCount > 0 && `${subAreaCount} subáreas · `}{node.documentCount} arquivos
      </div>
    </Link>
  )
}

function Pill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        height: 30, padding: '0 12px', borderRadius: 999, fontSize: 'var(--fs-small)', fontWeight: 500,
        border: '1px solid var(--ev-border)', cursor: 'pointer',
        background: active ? 'var(--ev-accent)' : 'var(--ev-bg-elevated)',
        color: active ? 'white' : 'var(--ev-fg)',
      }}
    >
      {children}
    </button>
  )
}
